import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPlanInfo, calculateNewExpiry } from '../utils/plan';

const CHECK = '\u2705';
const CROSS = '\u274C';

function PlanCard({ plan, title, monthlyPrice, yearlyPrice, badge, badgeColor, features, isCurrentPlan, isFeatured, borderColor, billingCycle, onSelect }) {
  return (
    <div style={{
      background: '#fff',
      border: `2px solid ${isFeatured ? borderColor : '#e2e8f0'}`,
      borderRadius: 16,
      padding: '28px 24px',
      flex: 1,
      minWidth: 240,
      maxWidth: 320,
      position: 'relative',
      boxShadow: isFeatured ? `0 8px 32px ${borderColor}30` : '0 2px 8px rgba(0,0,0,0.06)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {badge && (
        <div style={{
          position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
          background: badgeColor, color: '#fff',
          padding: '3px 16px', borderRadius: 99,
          fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
        }}>
          {badge}
        </div>
      )}

      <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#64748b' }}>
        {title}
      </div>
      <div style={{ fontSize: 32, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
        {plan === 'free' ? '\u20B90' : (billingCycle === 'yearly' ? yearlyPrice : monthlyPrice)}
      </div>
      {plan !== 'free' && (
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
          {billingCycle === 'yearly'
            ? `Save 30% vs monthly (\u20B9${monthlyPrice}/mo)`
            : `or ${yearlyPrice}/year (save 30%)`}
        </div>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
        {features.map(([checked, text], i) => (
          <li key={i} style={{
            fontSize: 13, padding: '5px 0',
            borderBottom: '1px solid #f8fafc',
            display: 'flex', gap: 8, alignItems: 'flex-start',
            color: checked ? '#374151' : '#94a3b8',
          }}>
            <span style={{ flexShrink: 0, width: 16 }}>{checked ? CHECK : CROSS}</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      {isCurrentPlan ? (
        <button disabled style={{
          width: '100%', padding: '12px 0', background: '#e2e8f0',
          color: '#64748b', border: 'none', borderRadius: 8,
          fontSize: 14, fontWeight: 700, cursor: 'default',
        }}>
          Current Plan
        </button>
      ) : plan === 'free' ? (
        <button disabled style={{
          width: '100%', padding: '12px 0', background: '#f1f5f9',
          color: '#94a3b8', border: 'none', borderRadius: 8,
          fontSize: 14, fontWeight: 700, cursor: 'default',
        }}>
          Free Forever
        </button>
      ) : (
        <button
          onClick={() => onSelect(plan, billingCycle)}
          style={{
            width: '100%', padding: '12px 0',
            background: isFeatured
              ? `linear-gradient(135deg, ${borderColor}, ${borderColor}cc)`
              : '#f1f5f9',
            color: isFeatured ? '#fff' : '#374151',
            border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>
          {isCurrentPlan ? 'Current Plan' : 'Subscribe \u2192'}
        </button>
      )}
    </div>
  );
}

export default function Upgrade({ currentPlan = 'free' }) {
  const navigate = useNavigate();
  const { customer } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showContact, setShowContact] = useState(null); // null or { plan, cycle, newExpiry }

  const info = getPlanInfo(customer);

  const handleSelect = (plan, cycle) => {
    const newExpiry = calculateNewExpiry(customer?.trial_ends_at, cycle);
    setShowContact({ plan, cycle, newExpiry });
  };

  const plans = [
    {
      plan: 'free',
      title: 'Free Forever',
      monthlyPrice: '\u20B90/month',
      yearlyPrice: null,
      badge: null,
      badgeColor: '#6b7280',
      borderColor: '#6b7280',
      isFeatured: false,
      features: [
        [true,  'Screenshots every 30 mins'],
        [true,  'Basic website history (20 URLs)'],
        [true,  'App usage tracking'],
        [true,  'Bad content alerts'],
        [false, 'Screen time limits'],
        [false, 'Bedtime control'],
        [false, 'Hide app icon \uD83D\uDD12'],
        [false, 'WhatsApp alerts'],
        [false, 'Weekly reports'],
        [false, 'Delete screenshots'],
      ],
    },
    {
      plan: 'plus',
      title: 'Plus',
      monthlyPrice: '\u20B9499/mo',
      yearlyPrice: '\u20B94,199/yr',
      badge: '\u2B50 MOST POPULAR',
      badgeColor: '#2563eb',
      borderColor: '#2563eb',
      isFeatured: true,
      features: [
        [true, 'Everything in Free'],
        [true, 'Screenshots every 10 mins'],
        [true, 'Unlimited website history'],
        [true, 'Screen time limits & alerts'],
        [true, 'Bedtime control'],
        [true, 'Hide app icon from child \uD83D\uDD12'],
        [true, 'WhatsApp alerts'],
        [true, 'Weekly reports & charts'],
        [true, 'Delete screenshots'],
        [true, '1 child'],
      ],
    },
    {
      plan: 'family',
      title: 'Family',
      monthlyPrice: '\u20B9799/mo',
      yearlyPrice: '\u20B96,699/yr',
      badge: '\uD83D\uDC51 BEST VALUE',
      badgeColor: '#7c3aed',
      borderColor: '#7c3aed',
      isFeatured: true,
      features: [
        [true, 'Everything in Plus'],
        [true, 'Screenshots every 5 mins'],
        [true, 'Up to 3 children'],
        [true, 'PDF reports'],
        [true, '90 days history'],
        [true, 'Priority support'],
        [true, 'WhatsApp alerts'],
        [true, 'Bedtime control'],
        [true, 'Screen time limits'],
        [true, 'Hide app icon \uD83D\uDD12'],
      ],
    },
  ];

  const faqs = [
    { q: 'Can I cancel anytime?', a: 'Yes, cancel anytime. After your subscription period ends, you\'ll be moved to the Free Forever plan automatically.' },
    { q: 'What happens after the trial?', a: 'Your account automatically moves to the Free Forever plan. No charges without your consent. You keep all features of the free plan.' },
    { q: 'What happens when my subscription expires?', a: 'Your plan automatically reverts to Free Forever. No data is deleted — upgrade again anytime to unlock premium features.' },
    { q: 'Can I renew before my plan expires?', a: 'Yes! If you renew before expiry, your subscription extends from the current end date — you don\'t lose any days.' },
    { q: 'Monthly or yearly — which is better?', a: 'Yearly saves you 30%. Monthly is flexible — cancel anytime. Both get the same features.' },
    { q: 'Is my child\'s data safe?', a: 'Yes. All data is stored securely on Supabase. We never sell your data.' },
    { q: 'Does my child know they\'re monitored?', a: 'The Free plan shows a shield icon. Paid plans can hide the icon entirely for silent monitoring.' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
          Protect your child. Pick your plan.
        </h1>
        <p style={{ fontSize: 15, color: '#64748b', margin: 0 }}>
          {info.isFree && info.isExpired
            ? 'Your previous plan expired. Renew to unlock premium features!'
            : 'Start with a 7-day free trial. No credit card required.'}
        </p>
      </div>

      {/* Current plan status */}
      {!info.isFree && (
        <div style={{
          background: info.daysLeft <= 3 ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${info.daysLeft <= 3 ? '#fca5a5' : '#86efac'}`,
          borderRadius: 10, padding: '12px 20px',
          textAlign: 'center', marginBottom: 20, fontSize: 14,
          color: info.daysLeft <= 3 ? '#991b1b' : '#166534',
          fontWeight: 600,
        }}>
          {info.isTrial
            ? `\uD83D\uDD52 Trial expires in ${info.daysLeft} day${info.daysLeft !== 1 ? 's' : ''} — upgrade to keep premium features!`
            : `\u2705 ${info.plan.charAt(0).toUpperCase() + info.plan.slice(1)} plan active — ${info.daysLeft} day${info.daysLeft !== 1 ? 's' : ''} remaining`}
        </div>
      )}

      {/* Billing toggle */}
      <div style={{
        display: 'flex', justifyContent: 'center', marginBottom: 28, gap: 0,
      }}>
        <button
          onClick={() => setBillingCycle('monthly')}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            border: '2px solid #e2e8f0', borderRadius: '8px 0 0 8px',
            background: billingCycle === 'monthly' ? '#0f172a' : '#fff',
            color: billingCycle === 'monthly' ? '#fff' : '#64748b',
          }}>
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('yearly')}
          style={{
            padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            border: '2px solid #e2e8f0', borderLeft: 'none', borderRadius: '0 8px 8px 0',
            background: billingCycle === 'yearly' ? '#0f172a' : '#fff',
            color: billingCycle === 'yearly' ? '#fff' : '#64748b',
          }}>
          Yearly (save 30%)
        </button>
      </div>

      {/* Annual savings banner */}
      {billingCycle === 'yearly' && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
          border: '1px solid #86efac',
          borderRadius: 10, padding: '12px 20px',
          textAlign: 'center', marginBottom: 28, fontSize: 14,
          color: '#166534', fontWeight: 600,
        }}>
          \uD83D\uDCB0 Save 30% with annual billing — Plus: \u20B94,199/year \u00B7 Family: \u20B96,699/year
        </div>
      )}

      {/* Plan cards */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 48 }}>
        {plans.map(p => (
          <PlanCard
            key={p.plan}
            {...p}
            billingCycle={billingCycle}
            isCurrentPlan={currentPlan === p.plan}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {/* How subscriptions work */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>How Subscriptions Work</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {[
            { icon: '\uD83C\uDD93', title: 'Free Trial', desc: 'Start with 7 days of full access. No payment needed. After 7 days, you move to Free Forever.' },
            { icon: '\uD83D\uDCB3', title: 'Monthly Plan', desc: 'Pay monthly. Active for 30 days. After that, back to Free Forever unless you renew.' },
            { icon: '\uD83D\uDCC5', title: 'Yearly Plan', desc: 'Pay yearly and save 30%. Active for 365 days. After that, back to Free Forever unless you renew.' },
            { icon: '\uD83D\uDD04', title: 'Renew Early', desc: 'Renew before your plan expires and your subscription extends from the current end date. No days lost!' },
          ].map(item => (
            <div key={item.title} style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 12, padding: '20px 16px',
            }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 6 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Feature comparison table */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>Full Feature Comparison</div>
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th style={{ textAlign: 'center' }}>Free</th>
              <th style={{ textAlign: 'center', color: '#2563eb' }}>Plus</th>
              <th style={{ textAlign: 'center', color: '#7c3aed' }}>Family</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Screenshot interval', '30 min', '10 min', '5 min'],
              ['Website history', '20 URLs', 'Unlimited', 'Unlimited'],
              ['History retention', '7 days', '30 days', '90 days'],
              ['Screen time limits', '\u274C', '\u2705', '\u2705'],
              ['Bedtime control', '\u274C', '\u2705', '\u2705'],
              ['Hide app icon', '\u274C', '\u2705', '\u2705'],
              ['WhatsApp alerts', '\u274C', '\u2705', '\u2705'],
              ['Weekly reports', '\u274C', '\u2705', '\u2705'],
              ['PDF reports', '\u274C', '\u274C', '\u2705'],
              ['Delete screenshots', '\u274C', '\u2705', '\u2705'],
              ['Children supported', '1', '1', '3'],
              ['Priority support', '\u274C', '\u274C', '\u2705'],
            ].map(([feature, free, plus, fam], i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{feature}</td>
                <td style={{ textAlign: 'center', color: '#64748b' }}>{free}</td>
                <td style={{ textAlign: 'center', color: '#2563eb', fontWeight: 600 }}>{plus}</td>
                <td style={{ textAlign: 'center', color: '#7c3aed', fontWeight: 600 }}>{fam}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>Frequently Asked Questions</div>
        {faqs.map(({ q, a }, i) => (
          <div key={i} style={{
            padding: '16px 0',
            borderBottom: i < faqs.length - 1 ? '1px solid #f1f5f9' : 'none',
          }}>
            <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 6, fontSize: 14 }}>
              {q}
            </div>
            <div style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6 }}>{a}</div>
          </div>
        ))}
      </div>

      {/* Supported Platforms */}
      <div className="card" style={{ marginBottom: 40 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>Supported Platforms</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
          {[
            { icon: '\uD83C\uDF4E', name: 'macOS', detail: 'Apple Silicon & Intel', available: true },
            { icon: '\uD83E\uDE9F', name: 'Windows', detail: 'Windows 10 & 11', available: false },
            { icon: '\uD83E\uDD16', name: 'Android', detail: 'Phone & Tablet', available: false },
            { icon: '\uD83D\uDCF1', name: 'iOS', detail: 'iPhone & iPad', available: false },
          ].map(p => (
            <div key={p.name} style={{
              background: p.available ? '#f0fdf4' : '#f8fafc',
              border: `2px solid ${p.available ? '#86efac' : '#e2e8f0'}`,
              borderRadius: 12, padding: '20px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{p.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', marginBottom: 2 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>{p.detail}</div>
              <span style={{
                fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99,
                background: p.available ? '#dcfce7' : '#fef3c7',
                color: p.available ? '#166534' : '#92400e',
              }}>
                {p.available ? '\u2705 AVAILABLE' : '\uD83D\uDD1C COMING SOON'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button className="btn-sm" onClick={() => navigate(-1)}>
          \u2190 Go back
        </button>
      </div>

      {/* Contact modal for subscription */}
      {showContact && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }} onClick={e => { if (e.target === e.currentTarget) setShowContact(null); }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32,
            maxWidth: 420, width: '100%', textAlign: 'center',
            boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>
              {showContact.plan === 'family' ? '\uD83D\uDC51' : '\u2B50'}
            </div>
            <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 20 }}>
              {info.isPaid || info.isTrial ? 'Upgrade to' : 'Subscribe to'} {showContact.plan.charAt(0).toUpperCase() + showContact.plan.slice(1)}
            </h3>

            {/* Subscription details */}
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 10, padding: 16, margin: '16px 0',
              textAlign: 'left', fontSize: 13, lineHeight: 1.8,
            }}>
              <div><strong>Plan:</strong> {showContact.plan.charAt(0).toUpperCase() + showContact.plan.slice(1)}</div>
              <div><strong>Billing:</strong> {showContact.cycle === 'yearly' ? 'Yearly' : 'Monthly'}</div>
              <div><strong>Price:</strong> {showContact.plan === 'plus'
                ? (showContact.cycle === 'yearly' ? '\u20B94,199/year' : '\u20B9499/month')
                : (showContact.cycle === 'yearly' ? '\u20B96,699/year' : '\u20B9799/month')
              }</div>
              <div><strong>Active until:</strong> {showContact.newExpiry.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {info.expiresAt && !info.isExpired && (
                <div style={{ color: '#16a34a', marginTop: 4 }}>
                  \u2705 Extends from your current plan end date!
                </div>
              )}
            </div>

            <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>
              To activate, contact us on WhatsApp or email. We'll set it up within minutes!
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <a
                href={`https://wa.me/919999999999?text=Hi! I want to subscribe to ParentsProtection ${showContact.plan} (${showContact.cycle}). My email: ${customer?.email || ''}`}
                target="_blank" rel="noreferrer"
                style={{
                  display: 'block', padding: '11px 0',
                  background: '#25d366', color: '#fff',
                  borderRadius: 8, fontWeight: 700, fontSize: 14,
                  textDecoration: 'none',
                }}>
                \uD83D\uDCAC WhatsApp Us
              </a>
              <a
                href={`mailto:support@parentsprotection.app?subject=Subscribe to ${showContact.plan} (${showContact.cycle})&body=Hi, I want to subscribe to the ${showContact.plan} plan (${showContact.cycle}). My account email: ${customer?.email || ''}`}
                style={{
                  display: 'block', padding: '11px 0',
                  background: '#e2e8f0', color: '#374151',
                  borderRadius: 8, fontWeight: 700, fontSize: 14,
                  textDecoration: 'none',
                }}>
                \uD83D\uDCE7 Email Us
              </a>
            </div>
            <button
              onClick={() => setShowContact(null)}
              style={{
                border: 'none', background: 'none', color: '#94a3b8',
                cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
              }}>
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`.btn-sm { margin-top: 0 !important; }`}</style>
    </div>
  );
}
